/**
 * Retry wrapper utility for handling API call failures with exponential backoff
 * @param {Function} fn - The async function to retry
 * @param {Object} options - Retry configuration options
 * @returns {Promise} - Result of the successful function call
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    retryableErrors = ['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND', 'rate_limit_exceeded', '429', '500', '502', '503', '504'],
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      const isRetryable = retryableErrors?.some(errCode => 
        error?.message?.includes(errCode) || 
        error?.code === errCode ||
        error?.status === parseInt(errCode) ||
        error?.error?.code === errCode
      );

      // If this is the last attempt or error is not retryable, throw immediately
      if (attempt === maxRetries || !isRetryable) {
        console.error(`API call failed after ${attempt + 1} attempts:`, error);
        throw error;
      }

      // Log retry attempt
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`, {
        error: error?.message,
        code: error?.code || error?.status,
      });

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));

      // Increase delay for next attempt, capped at maxDelay
      delay = Math.min(delay * backoffMultiplier, maxDelay);
    }
  }

  throw lastError;
}

/**
 * Specialized retry wrapper for OpenAI API calls
 * Handles specific OpenAI error codes and rate limiting
 */
export async function retryOpenAICall(fn) {
  return retryWithBackoff(fn, {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 15000,
    backoffMultiplier: 2,
    retryableErrors: [
      'rate_limit_exceeded',
      'insufficient_quota',
      'server_error',
      'timeout',
      '429', // Rate limit
      '500', // Internal server error
      '502', // Bad gateway
      '503', // Service unavailable
      '504', // Gateway timeout
      'ECONNRESET',
      'ETIMEDOUT',
      'ENOTFOUND',
    ],
  });
}

export default {
  retryWithBackoff,
  retryOpenAICall,
};